const cleanText = (value) =>
  String(value == null ? "" : value).trim();

const resolveSafeHttpLink = (value, fallback = "") => {
  const input = cleanText(value);

  try {
    const parsedUrl = new URL(input || fallback);

    return ["http:", "https:"].includes(parsedUrl.protocol)
      ? parsedUrl.toString()
      : fallback;
  } catch {
    return fallback;
  }
};

export const ONGROUND_APP_LINKS = Object.freeze({
  googlePlay:
    "https://play.google.com/store/apps/details?id=com.getonground.app",
  appStore:
    "https://apps.apple.com/in/app/onground-book-play-repeat/id6755392864",
});

export const resolveOngroundAppLink = (value, platform) =>
  resolveSafeHttpLink(
    value,
    ONGROUND_APP_LINKS[platform] || "",
  );

export const ONGROUND_SOCIAL_LINKS = Object.freeze({
  instagram: "https://www.instagram.com/onground.app",
  facebook: "https://www.facebook.com/ongroundapp",
  tiktok: "https://www.tiktok.com/@ongroundapp",
  whatsapp:
    "https://api.whatsapp.com/send?phone=9779700014042",
});

export const resolveOngroundSocialLink = (value, platform) =>
  resolveSafeHttpLink(
    value,
    ONGROUND_SOCIAL_LINKS[platform] || "",
  );

export const resolveVenueSocialLink = (value) =>
  resolveSafeHttpLink(value, "");

const isPlainObject = (value) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value);

const BLOCKED_CONFIGURATION_KEYS = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);

const isSafeConfigurationKey = (key) =>
  /^[A-Za-z][A-Za-z0-9_-]{0,79}$/.test(key) &&
  !BLOCKED_CONFIGURATION_KEYS.has(key);

export const normalizeStringMap = (value) => {
  const input = isPlainObject(value) ? value : {};

  return Object.entries(input).reduce(
    (result, [key, item]) => {
      if (!isSafeConfigurationKey(key)) {
        return result;
      }

      if (typeof item !== "string") {
        return result;
      }

      result[key] = item.trim();
      return result;
    },
    {},
  );
};

const normalizeStructuredValue = (value, depth = 0) => {
  if (depth > 5) return null;

  if (value === null || value === undefined) {
    return value ?? null;
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, 50)
      .map((item) => normalizeStructuredValue(item, depth + 1))
      .filter((item) => item !== null && item !== undefined);
  }

  if (!isPlainObject(value)) {
    return null;
  }

  return Object.entries(value)
    .slice(0, 100)
    .reduce((result, [key, item]) => {
      if (!isSafeConfigurationKey(key)) {
        return result;
      }

      const normalized = normalizeStructuredValue(
        item,
        depth + 1,
      );

      if (normalized !== null && normalized !== undefined) {
        result[key] = normalized;
      }

      return result;
    }, {});
};

export const normalizeWebsiteExtensions = (value) => {
  const input = isPlainObject(value) ? value : {};

  return {
    copy: normalizeStringMap(input.copy),
    media: normalizeStringMap(input.media),
    themeContent: isPlainObject(input.themeContent)
      ? normalizeStructuredValue(input.themeContent)
      : {},
  };
};

export const getThemeContent = (website, themeId) => {
  const themeContent = isPlainObject(website?.themeContent)
    ? website.themeContent
    : {};

  const content = themeContent?.[themeId];

  return isPlainObject(content) ? content : {};
};

export const resolveWebsiteCopy = (
  website,
  key,
  fallback = "",
) => cleanText(website?.copy?.[key]) || fallback;

export const resolveThemeCopy = (
  website,
  themeId,
  key,
  fallback = "",
  legacyValue = "",
) => {
  const content = getThemeContent(website, themeId);

  return (
    cleanText(content?.copy?.[key]) ||
    cleanText(legacyValue) ||
    fallback
  );
};

export const resolveWebsiteMedia = (
  website,
  key,
  fallback = "",
) => cleanText(website?.media?.[key]) || fallback;

export const resolveThemeMedia = (
  website,
  themeId,
  key,
  fallback = "",
) => {
  const content = getThemeContent(website, themeId);

  return cleanText(content?.media?.[key]) || fallback;
};

const LEGACY_GLOBAL_LOGO_THEME_IDS = new Set([
  "arena",
  "pickle-point-nepal",
  "merlion-gymnasium",
  "bhaisepati-sports-academy",
  "turf-park",
]);

export const resolveThemeLogo = (
  website,
  themeId,
  fallback = "",
) => {
  const normalizedThemeId = cleanText(themeId);
  const legacyLogo =
    LEGACY_GLOBAL_LOGO_THEME_IDS.has(normalizedThemeId) &&
    cleanText(website?.theme?.layout) === normalizedThemeId
      ? cleanText(website?.media?.logoUrl)
      : "";

  return resolveThemeMedia(
    website,
    normalizedThemeId,
    "logoUrl",
    legacyLogo || fallback,
  );
};

export const resolveThemePalette = (
  website,
  themeId,
  fallback = {},
) => {
  const normalizedThemeId = cleanText(themeId);
  const content = getThemeContent(website, normalizedThemeId);
  const configuredPalette = isPlainObject(content?.palette)
    ? content.palette
    : {};
  const useLegacyTheme =
    cleanText(website?.theme?.layout) === normalizedThemeId;
  const legacyTheme =
    useLegacyTheme && isPlainObject(website?.theme)
      ? website.theme
      : {};

  return {
    primaryColor:
      cleanText(configuredPalette.primaryColor) ||
      cleanText(legacyTheme.primaryColor) ||
      cleanText(fallback?.primaryColor) ||
      "#33A444",
    accentColor:
      cleanText(configuredPalette.accentColor) ||
      cleanText(legacyTheme.accentColor) ||
      cleanText(fallback?.accentColor) ||
      "#111827",
    backgroundStyle:
      cleanText(configuredPalette.backgroundStyle) ||
      cleanText(legacyTheme.backgroundStyle) ||
      cleanText(fallback?.backgroundStyle) ||
      "clean",
  };
};

export const resolveThemeHeroValue = (
  website,
  themeId,
  key,
  fallback = "",
) => {
  const normalizedThemeId = cleanText(themeId);
  const content = getThemeContent(website, normalizedThemeId);
  const configuredHero = isPlainObject(content?.hero)
    ? content.hero
    : {};
  const useLegacyHero =
    cleanText(website?.theme?.layout) === normalizedThemeId;
  const legacyHero =
    useLegacyHero && isPlainObject(website?.hero)
      ? website.hero
      : {};

  return (
    cleanText(configuredHero?.[key]) ||
    cleanText(legacyHero?.[key]) ||
    fallback
  );
};

export const resolveThemeBoolean = (
  website,
  themeId,
  key,
  fallback = true,
) => {
  const content = getThemeContent(website, themeId);
  const value = content?.visibility?.[key];

  return typeof value === "boolean" ? value : fallback;
};

export const resolveThemeArray = (
  website,
  themeId,
  key,
  fallback = [],
  legacyValue = [],
) => {
  const content = getThemeContent(website, themeId);
  const configured = content?.[key];

  if (Array.isArray(configured) && configured.length > 0) {
    return configured;
  }

  if (Array.isArray(legacyValue) && legacyValue.length > 0) {
    return legacyValue;
  }

  return Array.isArray(fallback) ? fallback : [];
};

export const resolveThemeObject = (
  website,
  themeId,
  key,
  fallback = {},
) => {
  const content = getThemeContent(website, themeId);
  const configured = content?.[key];

  return isPlainObject(configured)
    ? configured
    : fallback;
};

const field = (
  key,
  label,
  fallback,
  options = {},
) => ({
  key,
  label,
  fallback,
  maxLength: options.maxLength || 120,
  multiline: options.multiline === true,
  type: options.type || "text",
  hint: cleanText(options.hint),
  builderVisible: options.builderVisible !== false,
  themeLayouts: Array.isArray(options.themeLayouts)
    ? Object.freeze([
        ...new Set(
          options.themeLayouts
            .map(cleanText)
            .filter(Boolean),
        ),
      ])
    : null,
});

export const isWebsiteConfigurationFieldVisible = (
  fieldDefinition,
  layout,
) => {
  if (
    !fieldDefinition ||
    fieldDefinition.builderVisible === false
  ) {
    return false;
  }

  const themeLayouts = Array.isArray(
    fieldDefinition.themeLayouts,
  )
    ? fieldDefinition.themeLayouts
    : [];

  return (
    themeLayouts.length === 0 ||
    themeLayouts.includes(cleanText(layout))
  );
};

export const hasWebsiteConfigurationOverride = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  return typeof value === "string"
    ? cleanText(value).length > 0
    : true;
};

export const resolveWebsiteConfigurationEditorValue = (
  configuredValue,
  effectiveFallback = "",
) =>
  hasWebsiteConfigurationOverride(configuredValue)
    ? configuredValue
    : effectiveFallback ?? "";

export const COMMON_COPY_GROUPS = Object.freeze([
  {
    id: "general",
    title: "General website text",
    fields: Object.freeze([
      field("heroHeadlineFallback", "Hero headline fallback", "Book your favourite sports venue", { maxLength: 100 }),
      field(
        "heroSubheadlineFallback",
        "Hero description fallback",
        "Explore facilities, available grounds, timings, pricing, and book your slot online.",
        { multiline: true, maxLength: 320 },
      ),
      field("heroCtaFallback", "Hero button fallback", "Book Now", { maxLength: 40 }),
      field("seoTitleFallback", "SEO title fallback", "Venue Booking", { maxLength: 100 }),
      field("seoDescriptionFallback", "SEO description fallback", "Book sports venues online.", { multiline: true, maxLength: 240 }),
    ]),
  },
  {
    id: "navigation",
    title: "Navigation labels",
    fields: Object.freeze([
      field("navHome", "Home navigation", "Home", { maxLength: 24 }),
      field("navAbout", "About navigation", "About", { maxLength: 24 }),
      field("navFacilities", "Facilities navigation", "Facilities", { maxLength: 24 }),
      field("navGrounds", "Grounds navigation", "Grounds", { maxLength: 24 }),
      field("navGallery", "Gallery navigation", "Gallery", { maxLength: 24 }),
      field("navPricing", "Pricing navigation", "Pricing", { maxLength: 24 }),
      field("navLocation", "Location navigation", "Location", { maxLength: 24 }),
      field("navBooking", "Booking navigation", "Booking", { maxLength: 24 }),
      field("navContact", "Contact navigation", "Contact", { maxLength: 24 }),
    ]),
  },
  {
    id: "shared-sections",
    title: "Shared section text",
    fields: Object.freeze([
      field("aboutEyebrow", "About eyebrow", "About", { maxLength: 40 }),
      field("aboutTitle", "About title", "About this venue", { maxLength: 80 }),
      field(
        "aboutEmptyText",
        "About fallback message",
        "Venue details will be available soon.",
        { multiline: true, maxLength: 250 },
      ),
      field("galleryEyebrow", "Gallery eyebrow", "Gallery", { maxLength: 40 }),
      field("galleryTitle", "Gallery title", "Explore the venue", { maxLength: 80 }),
      field(
        "galleryEmptyText",
        "Gallery empty message",
        "Gallery images are not available yet.",
        { maxLength: 180 },
      ),
      field("facilitiesEyebrow", "Facilities eyebrow", "Facilities", { maxLength: 40 }),
      field("facilitiesTitle", "Facilities title", "What customers get", { maxLength: 80 }),
      field(
        "facilitiesEmptyText",
        "Facilities empty message",
        "Facilities are not added yet.",
        { maxLength: 180 },
      ),
      field("groundsEyebrow", "Grounds eyebrow", "Grounds", { maxLength: 40 }),
      field("groundsTitle", "Grounds title", "Choose your play area", { maxLength: 80 }),
      field("groundFallbackName", "Ground fallback name", "Ground", { maxLength: 50 }),
      field("groundCapacityLabel", "Ground capacity label", "Capacity", { maxLength: 30 }),
      field(
        "groundsEmptyText",
        "Grounds empty message",
        "No active grounds are available yet.",
        { maxLength: 180 },
      ),
      field("priceOnRequestLabel", "Price-on-request label", "Price on request", { maxLength: 50 }),
      field("pricingEyebrow", "Pricing eyebrow", "Pricing", { maxLength: 40 }),
      field("pricingTitle", "Pricing title", "Simple pricing overview", { maxLength: 80 }),
      field("pricingStartingLabel", "Starting-price label", "Starting from", { maxLength: 50 }),
      field(
        "pricingDescription",
        "Pricing description",
        "Final slot pricing is calculated during booking based on selected ground and time.",
        { multiline: true, maxLength: 300 },
      ),
      field("bookingEyebrow", "Booking eyebrow", "Booking", { maxLength: 40 }),
      field("bookingTitle", "Booking title", "Book your slot online", { maxLength: 80 }),
      field("bookingCardTitle", "Booking card title", "Ready to play?", { maxLength: 80 }),
      field(
        "bookingCardDescription",
        "Booking card description",
        "Select a ground, date, and available time slot. We will temporarily hold the slot while payment is completed.",
        { multiline: true, maxLength: 350 },
      ),
      field("bookingSelectLabel", "Booking button label", "Select Slot", { maxLength: 32 }),
      field("locationEyebrow", "Location eyebrow", "Location", { maxLength: 40 }),
      field("locationTitle", "Location title", "Find the venue", { maxLength: 80 }),
      field("addressUnavailableText", "Missing-address message", "Address not available", { maxLength: 160 }),
      field(
        "locationUnavailableText",
        "Missing-location message",
        "Location details not available",
        { maxLength: 160 },
      ),
      field("openMapsLabel", "Map button label", "Open in Maps", { maxLength: 32 }),
      field("policiesEyebrow", "Policies eyebrow", "Policies", { maxLength: 40 }),
      field("policiesTitle", "Policies title", "Rules and cancellation", { maxLength: 80 }),
      field("cancellationTitle", "Cancellation title", "Cancellation", { maxLength: 50 }),
      field("cancellationEmptyText", "Cancellation fallback", "Policy not added yet.", { maxLength: 180 }),
      field("rulesTitle", "Rules title", "Rules & Terms", { maxLength: 50 }),
      field("rulesEmptyText", "Rules fallback", "Rules are not added yet.", { maxLength: 180 }),
      field("contactEyebrow", "Contact eyebrow", "Contact", { maxLength: 40 }),
      field("contactTitle", "Contact title", "Need help?", { maxLength: 80 }),
      field("contactFallbackLabel", "Contact fallback label", "Venue contact", { maxLength: 70 }),
      field(
        "contactUnavailableText",
        "Contact unavailable message",
        "Contact number not available",
        { maxLength: 160 },
      ),
    ]),
  },
  {
    id: "payment-status",
    title: "Common payment-result pages",
    fields: Object.freeze([
      field(
        "paymentResultBrandLabel",
        "Payment-page brand label",
        "Secure booking by Onground",
        { maxLength: 70 },
      ),
      field(
        "paymentResultPoweredByLabel",
        "Payment-page footer label",
        "Powered by Onground",
        { maxLength: 70 },
      ),
      field("paymentSuccessEyebrow", "Success eyebrow", "Confirmed", { maxLength: 40 }),
      field("paymentSuccessTitle", "Success title", "You're on the list.", { maxLength: 90 }),
      field(
        "paymentSuccessMessage",
        "Success message",
        "Your payment was verified and your booking is confirmed.",
        { multiline: true, maxLength: 300 },
      ),
      field("paymentReferencePrefix", "Booking-reference prefix", "Reference", { maxLength: 40 }),
      field("paymentReferenceLabel", "Shared-reference label", "Reference", { maxLength: 40 }),
      field("paymentWhenLabel", "Booking-date label", "When", { maxLength: 30 }),
      field("paymentWhereLabel", "Booking-location label", "Where", { maxLength: 30 }),
      field("paymentBackToVenueLabel", "Back-to-venue button", "Back to venue", { maxLength: 40 }),
      field("paymentShareLabel", "Share button", "Share booking", { maxLength: 40 }),
      field("paymentCopiedLabel", "Copied confirmation", "Copied", { maxLength: 30 }),
      field("paymentShareHeading", "Shared-booking heading", "Booking confirmed", { maxLength: 70 }),
      field(
        "paymentConfirmationTitle",
        "Confirmation guidance title",
        "Keep your confirmation",
        { maxLength: 70 },
      ),
      field(
        "paymentConfirmationMessage",
        "Confirmation guidance message",
        "Take a screenshot or download this confirmation. Download the Onground App to find and manage your reservation.",
        { multiline: true, maxLength: 300 },
      ),
      field(
        "paymentDownloadConfirmationLabel",
        "Download-confirmation button",
        "Download confirmation",
        { maxLength: 60 },
      ),
      field(
        "paymentDownloadedLabel",
        "Downloaded confirmation",
        "Downloaded",
        { maxLength: 30 },
      ),
      field(
        "paymentDownloadOngroundLabel",
        "App-download button prefix",
        "Download Onground App",
        { maxLength: 60 },
      ),
      field(
        "paymentGooglePlayLabel",
        "Google Play button label",
        "Google Play",
        { maxLength: 40 },
      ),
      field(
        "paymentAppStoreLabel",
        "App Store button label",
        "App Store",
        { maxLength: 40 },
      ),
      field(
        "paymentSuccessFooterNote",
        "Success footer note",
        "Please keep your booking reference for check-in and support.",
        { multiline: true, maxLength: 260 },
      ),
      field("paymentPendingEyebrow", "Pending eyebrow", "Verification pending", { maxLength: 50 }),
      field("paymentPendingTitle", "Pending title", "We're confirming your payment.", { maxLength: 90 }),
      field(
        "paymentPendingMessage",
        "Pending message",
        "Your payment is still being verified. Please check your booking again shortly.",
        { multiline: true, maxLength: 300 },
      ),
      field("paymentFailedEyebrow", "Failure eyebrow", "Payment failed", { maxLength: 50 }),
      field("paymentFailedTitle", "Failure title", "We couldn't process that payment.", { maxLength: 100 }),
      field(
        "paymentFailedMessage",
        "Failure message",
        "No amount should be debited for an incomplete payment. If money was deducted, eSewa or your bank should reverse it automatically.",
        { multiline: true, maxLength: 380 },
      ),
      field("paymentFailureReasonsTitle", "Failure-reasons title", "Common reasons", { maxLength: 60 }),
      field(
        "paymentFailureReasonOne",
        "Failure reason 1",
        "Insufficient balance in the eSewa wallet or linked account",
        { maxLength: 180 },
      ),
      field(
        "paymentFailureReasonTwo",
        "Failure reason 2",
        "eSewa or the bank service was temporarily unavailable",
        { maxLength: 180 },
      ),
      field(
        "paymentFailureReasonThree",
        "Failure reason 3",
        "The payment was cancelled or not completed in time",
        { maxLength: 180 },
      ),
      field("paymentTryAgainLabel", "Try-again button", "Try again", { maxLength: 40 }),
      field("paymentContactSupportLabel", "Contact-support button", "Contact support", { maxLength: 50 }),
      field("paymentErrorEyebrow", "Verification-error eyebrow", "Verification issue", { maxLength: 50 }),
      field("paymentErrorTitle", "Verification-error title", "We couldn't confirm the payment result.", { maxLength: 110 }),
      field(
        "paymentErrorMessage",
        "Verification-error message",
        "Please contact the venue before trying again so the booking can be checked safely.",
        { multiline: true, maxLength: 320 },
      ),
      field("websiteUnpublishedTitle", "Unpublished title", "Website not published", { maxLength: 70 }),
      field(
        "websiteUnpublishedDescription",
        "Unpublished description",
        "This venue website is currently not available for public visitors.",
        { multiline: true, maxLength: 260 },
      ),
    ]),
  },
  {
    id: "booking-modal",
    title: "Booking modal text",
    fields: Object.freeze([
      field("weekdaySun", "Sunday short label", "Sun", { maxLength: 10 }),
      field("weekdayMon", "Monday short label", "Mon", { maxLength: 10 }),
      field("weekdayTue", "Tuesday short label", "Tue", { maxLength: 10 }),
      field("weekdayWed", "Wednesday short label", "Wed", { maxLength: 10 }),
      field("weekdayThu", "Thursday short label", "Thu", { maxLength: 10 }),
      field("weekdayFri", "Friday short label", "Fri", { maxLength: 10 }),
      field("weekdaySat", "Saturday short label", "Sat", { maxLength: 10 }),
      field("modalTitleWithGroundPrefix", "Modal title prefix", "Book", { maxLength: 24 }),
      field("modalTitleWithoutGround", "Modal title without ground", "Book a Slot", { maxLength: 60 }),
      field("modalEyebrow", "Modal eyebrow", "Secure online booking", { maxLength: 60 }),
      field("modalHeadingPrefix", "Modal heading prefix", "Book", { maxLength: 24 }),
      field(
        "modalDescription",
        "Modal description",
        "Select an available slot, enter your details, and complete payment through Onground’s secure booking flow.",
        { multiline: true, maxLength: 320 },
      ),
      field("stepOneShortTitle", "Progress step 1 title", "Slot", { maxLength: 24 }),
      field("stepOneShortDescription", "Progress step 1 description", "Choose date and time", { maxLength: 50 }),
      field("stepTwoShortTitle", "Progress step 2 title", "Details", { maxLength: 24 }),
      field("stepTwoShortDescription", "Progress step 2 description", "Enter your information", { maxLength: 60 }),
      field("stepThreeShortTitle", "Progress step 3 title", "Pay", { maxLength: 24 }),
      field("stepThreeShortDescription", "Progress step 3 description", "Complete payment", { maxLength: 60 }),
      field("stepOneLabel", "Step 1 label", "Step 1", { maxLength: 24 }),
      field("stepOneTitle", "Step 1 title", "Choose your slot", { maxLength: 70 }),
      field(
        "stepOneDescription",
        "Step 1 description",
        "All availability and pricing below come directly from the venue.",
        { multiline: true, maxLength: 260 },
      ),
      field("groundLabel", "Ground label", "Ground", { maxLength: 40 }),
      field("previousMonthLabel", "Previous-month accessibility label", "Previous month", { maxLength: 60 }),
      field("nextMonthLabel", "Next-month accessibility label", "Next month", { maxLength: 60 }),
      field("availableTimesLabel", "Available-times label", "Available times", { maxLength: 60 }),
      field(
        "selectedGroundSmallLabel",
        "Selected-ground small label",
        "Selected ground",
        {
          maxLength: 60,
          builderVisible: false,
        },
      ),
      field("slotUnavailableLabel", "Unavailable-slot label", "Unavailable", { maxLength: 40 }),
      field("bookingProgressAriaLabel", "Booking-progress accessibility label", "Booking progress", { maxLength: 80 }),
      field("loadingSlotsText", "Loading-slots message", "Loading available slots...", { maxLength: 120 }),
      field("noSlotsText", "No-slots message", "No slots are available for this date.", { maxLength: 180 }),
      field("allSlotsBookedTitle", "All-slots-booked title", "All slots are booked for today!", { maxLength: 100 }),
      field("allSlotsBookedDescription", "All-slots-booked description", "Try another date or venue!", { maxLength: 120 }),
      field("cancelLabel", "Cancel button", "Cancel", { maxLength: 32 }),
      field("continueDetailsLabel", "Continue-to-details button", "Continue to Details", { maxLength: 40 }),
      field("stepTwoLabel", "Step 2 label", "Step 2", { maxLength: 24 }),
      field("detailsTitle", "Customer-details title", "Your details", { maxLength: 70 }),
      field(
        "detailsDescription",
        "Customer-details description",
        "These details are used for your reservation and payment confirmation.",
        { multiline: true, maxLength: 260 },
      ),
      field("fullNameLabel", "Full-name label", "Full name", { maxLength: 50 }),
      field("fullNamePlaceholder", "Full-name placeholder", "Enter your full name", { maxLength: 80 }),
      field("phoneLabel", "Phone label", "Phone", { maxLength: 50 }),
      field("optionalLabel", "Optional label", "(optional)", { maxLength: 30 }),
      field("phonePlaceholder", "Phone placeholder", "9876543210", { maxLength: 40 }),
      field("emailLabel", "Email label", "Email", { maxLength: 50 }),
      field("emailPlaceholder", "Email placeholder", "customer@example.com", { maxLength: 100 }),
      field("confirmationTitle", "Booking-confirmation title", "Booking confirmation", { maxLength: 70 }),
      field(
        "confirmationDescription",
        "Booking-confirmation description",
        "Enter an email address to receive your booking confirmation after successful payment.",
        { multiline: true, maxLength: 300 },
      ),
      field("backLabel", "Back button", "Back", { maxLength: 32 }),
      field("creatingHoldLabel", "Creating-hold label", "Creating Hold...", { maxLength: 50 }),
      field("continuePayLabel", "Continue-to-pay button", "Continue to Pay", { maxLength: 40 }),
      field("stepThreeLabel", "Step 3 label", "Step 3", { maxLength: 24 }),
      field("paymentTitle", "Payment-step title", "Complete payment", { maxLength: 70 }),
      field(
        "paymentDescription",
        "Payment-step description",
        "Your selected slot is now temporarily reserved.",
        { multiline: true, maxLength: 220 },
      ),
      field("holdCreatedTitle", "Hold-created title", "Your reservation hold was created", { maxLength: 100 }),
      field("holdPrefix", "Hold countdown prefix", "Complete payment within", { maxLength: 80 }),
      field("holdDefaultPeriod", "Default hold-period label", "the configured hold period", { maxLength: 80 }),
      field(
        "holdSuffix",
        "Hold countdown suffix",
        ". The slot is released automatically when the timer reaches 00:00.",
        { multiline: true, maxLength: 220 },
      ),
      field("secureEsewaTitle", "eSewa payment title", "Pay securely with eSewa", { maxLength: 80 }),
      field("esewaLogoAlt", "eSewa logo alternative text", "eSewa", { maxLength: 50 }),
      field("securePaymentTitle", "Generic payment title", "Secure online payment", { maxLength: 80 }),
      field(
        "esewaGatewayDescription",
        "eSewa payment description",
        "You’ll be redirected to eSewa to complete your payment securely.",
        { multiline: true, maxLength: 260 },
      ),
      field(
        "gatewayDescription",
        "Generic gateway description",
        "You’ll be redirected to the secure payment gateway to complete your payment.",
        { multiline: true, maxLength: 260 },
      ),
      field("paymentOrderCreatedText", "Order-created label", "Payment order created", { maxLength: 80 }),
      field("gatewayLabel", "Gateway label", "Gateway:", { maxLength: 40 }),
      field("amountLabel", "Amount label", "Amount:", { maxLength: 40 }),
      field("razorpayOrderLabel", "Razorpay order label", "Razorpay Order:", { maxLength: 60 }),
      field("payEsewaLabel", "Pay-with-eSewa button", "Pay with eSewa", { maxLength: 40 }),
      field("redirectingEsewaLabel", "eSewa redirecting label", "Redirecting to eSewa...", { maxLength: 60 }),
      field("closeLabel", "Close button", "Close", { maxLength: 32 }),
      field("creatingPaymentLabel", "Creating-payment label", "Creating Payment...", { maxLength: 60 }),
      field("paymentOrderCreatedButtonLabel", "Order-created button label", "Payment Order Created", { maxLength: 60 }),
      field("continuePaymentLabel", "Continue-payment button", "Continue to Payment", { maxLength: 60 }),
      field("summaryTitle", "Booking-summary title", "Booking summary", { maxLength: 70 }),
      field("currentDateLabel", "Current-date label", "Current date", { maxLength: 50 }),
      field("selectedSlotsLabel", "Selected-slots label", "Selected slots", { maxLength: 60 }),
      field("selectedSlotsEmptyText", "Selected-slots empty message", "Your selected slots will appear here.", { maxLength: 180 }),
      field("totalLabel", "Total label", "Total", { maxLength: 40 }),
      field("reservationHoldLabel", "Reservation-hold label", "Reservation hold", { maxLength: 60 }),
      field(
        "confirmationConsentText",
        "Confirmation consent text",
        "By continuing, you confirm that the booking details above are correct.",
        { multiline: true, maxLength: 260 },
      ),
      field("selectDateFallback", "Select-date fallback", "Select a date", { maxLength: 60 }),
      field("slotFallbackLabel", "Slot fallback label", "Slot", { maxLength: 40 }),
      field("selectedGroundFallback", "Selected-ground fallback", "Selected ground", { maxLength: 60 }),
      field("slotsLoadErrorText", "Slots-load error fallback", "Unable to load available slots.", { maxLength: 180 }),
      field(
        "slotNoLongerAvailableText",
        "Slot-unavailable API message",
        "Slot no longer available",
        {
          maxLength: 180,
          builderVisible: false,
        },
      ),
      field("validationGround", "Select-ground validation", "Please select a ground.", { maxLength: 140 }),
      field("validationDate", "Select-date validation", "Please select a date.", { maxLength: 140 }),
      field("validationSlot", "Select-slot validation", "Please select at least one available slot.", { maxLength: 180 }),
      field("validationName", "Customer-name validation", "Please enter customer name.", { maxLength: 160 }),
      field("validationPhone", "Customer-phone validation", "Please enter customer phone number.", { maxLength: 180 }),
      field(
        "validationPhoneFormat",
        "Phone-format validation",
        "Please enter phone number using digits only, without country code plus sign.",
        { multiline: true, maxLength: 240 },
      ),
      field("validationEmail", "Customer-email validation", "Please enter customer email.", { maxLength: 180 }),
      field("validationEmailFormat", "Email-format validation", "Please enter a valid email address.", { maxLength: 180 }),
      field(
        "holdExpiredText",
        "Expired-hold message",
        "Your reservation hold has expired. Please select the slot again.",
        { multiline: true, maxLength: 260 },
      ),
      field(
        "holdExpiredReleasedText",
        "Expired-and-released message",
        "Your reservation hold expired. The slot is available to other customers again. Please select a slot to continue.",
        { multiline: true, maxLength: 320 },
      ),
      field("missingEsewaDetailsText", "Missing-eSewa-details message", "eSewa payment details are missing.", { maxLength: 180 }),
      field(
        "missingEsewaTransactionText",
        "Missing-eSewa-transaction message",
        "The eSewa transaction reference is missing. Please create a new payment request.",
        { multiline: true, maxLength: 260 },
      ),
      field(
        "esewaRequestAlreadySubmittedText",
        "Already-submitted eSewa request message",
        "This eSewa payment request was already submitted. Create a new payment request to try again.",
        { multiline: true, maxLength: 300 },
      ),
      field(
        "esewaRedirectErrorText",
        "eSewa redirect error message",
        "Unable to open eSewa. Please create a new payment request and try again.",
        { multiline: true, maxLength: 260 },
      ),
      field(
        "missingReservationHoldText",
        "Missing-reservation-hold message",
        "Reservation hold is missing. Please create the hold again.",
        { multiline: true, maxLength: 240 },
      ),
      field("paymentOrderErrorText", "Payment-order error fallback", "Unable to create payment order.", { maxLength: 180 }),
      field(
        "slotConflictText",
        "Slot-conflict message",
        "One or more selected slots are no longer available. Please choose another time. Your contact details have been kept.",
        { multiline: true, maxLength: 340 },
      ),
      field(
        "slotRefreshErrorText",
        "Slot-refresh error message",
        "The selected slot is no longer available, and availability could not be refreshed. Please select the date again. Your contact details have been kept.",
        { multiline: true, maxLength: 400 },
      ),
      field("holdCreateErrorText", "Hold-creation error fallback", "Unable to create reservation hold.", { maxLength: 180 }),
    ]),
  },
]);

export const COMMON_MEDIA_FIELDS = Object.freeze([
  field(
    "logoUrl",
    "Venue logo URL",
    "",
    {
      type: "url",
      maxLength: 1000,
      themeLayouts: [
        "arena",
        "pickle-point-nepal",
        "merlion-gymnasium",
        "bhaisepati-sports-academy",
        "turf-park",
      ],
      hint:
        "Shown in the public website navigation and footer where the theme supports a venue logo. Leave empty to keep the theme’s existing generated brand mark.",
    },
  ),
  field(
    "heroImageUrl",
    "Arena hero image URL",
    "",
    {
      type: "url",
      maxLength: 1000,
      themeLayouts: ["arena"],
      hint:
        "Overrides the Arena hero image. Leave empty to use venue/ground API media, then the exact original bundled Arena hero image.",
    },
  ),
]);

export const BOX_ARENA_COPY_GROUPS = Object.freeze([
  {
    id: "box-navigation-hero",
    title: "Navigation and hero",
    fields: Object.freeze([
      field("navGrounds", "Grounds navigation", "Grounds", { maxLength: 24 }),
      field("navAbout", "About navigation", "About", { maxLength: 24 }),
      field("navBooking", "Booking navigation", "Booking", { maxLength: 24 }),
      field("navContact", "Contact navigation", "Contact", { maxLength: 24 }),
      field("logoAlt", "Logo alternative text", "BoxArena Logo", { maxLength: 80 }),
      field("heroTitleLineOne", "Hero title line 1", "PLAY.", { maxLength: 30 }),
      field("heroTitleHighlight", "Hero highlighted line", "EAT.", { maxLength: 30 }),
      field("heroTitleLineThree", "Hero title line 3", "REPEAT.", { maxLength: 30 }),
      field("heroExploreLabel", "Hero explore button", "Explore Grounds →", { maxLength: 40 }),
      field("heroBadgeOne", "Hero badge 1", "🏟️ 3 Premium Grounds", { maxLength: 60 }),
      field("heroBadgeTwo", "Hero badge 2", "⚡ Instant Booking", { maxLength: 60 }),
      field("heroBadgeThree", "Hero badge 3", "🌙 Open Every Day", { maxLength: 60 }),
      field("heroLocationBadge", "Hero location badge", "", { maxLength: 120 }),
      field("scrollLabel", "Scroll label", "Scroll", { maxLength: 30 }),
      field(
        "heroDescriptionFallback",
        "Hero-description fallback",
        "Kathmandu's premier multi-sport arena — floodlit courts, all-weather turf, instant booking. Your game starts here.",
        { multiline: true, maxLength: 320 },
      ),
    ]),
  },
  {
    id: "box-grounds-about",
    title: "Grounds and about",
    fields: Object.freeze([
      field("groundsEyebrow", "Grounds eyebrow", "// WHAT WE OFFER", { maxLength: 50 }),
      field("groundsTitle", "Grounds title", "CHOOSE YOUR SPORT.", { maxLength: 80 }),
      field(
        "groundsDescription",
        "Grounds description",
        "Three elite grounds. One venue. Book in under 30 seconds.",
        { multiline: true, maxLength: 260 },
      ),
      field("groundBookLabel", "Ground booking button", "Book Now →", { maxLength: 40 }),
      field("groundFallbackNamePrefix", "Ground fallback-name prefix", "Ground", { maxLength: 40 }),
      field("aboutOpenBadge", "About image badge", "Open 7 Days a Week", { maxLength: 80 }),
      field("aboutEyebrow", "About eyebrow", "// ABOUT BOXARENA", { maxLength: 60 }),
      field("aboutTitleLineOne", "About title line 1", "WHERE CHAMPIONS", { maxLength: 60 }),
      field("aboutTitleLineTwo", "About title line 2", "ARE MADE.", { maxLength: 60 }),
      field(
        "aboutParagraphOne",
        "About paragraph 1",
        "BoxArena is Kathmandu's most complete multi-sport facility. Built for players who demand the best — from FIFA-grade artificial turf to tournament-ready pickleball courts, we provide elite playing conditions so you can focus on your game.",
        { multiline: true, maxLength: 600 },
      ),
      field(
        "aboutParagraphTwo",
        "About paragraph 2",
        "Located at Tangal-02, Kathmandu, we're open every day of the week with floodlights for night sessions, free parking, and equipment rental on-site.",
        { multiline: true, maxLength: 500 },
      ),
    ]),
  },
  {
    id: "box-contact-form",
    title: "Enquiry form",
    fields: Object.freeze([
      field("bookingEyebrow", "Enquiry eyebrow", "// GET IN TOUCH", { maxLength: 50 }),
      field("bookingTitleLineOne", "Enquiry title line 1", "LET'S", { maxLength: 40 }),
      field("bookingTitleLineTwo", "Enquiry title line 2", "TALK.", { maxLength: 40 }),
      field(
        "bookingDescription",
        "Enquiry description",
        "Have a question or want to discuss a corporate event? Send us a message and our team will get back to you shortly.",
        { multiline: true, maxLength: 360 },
      ),
      field(
        "bookingQuote",
        "Enquiry testimonial",
        '"The BoxArena team was incredibly helpful in organizing our annual corporate futsal tournament. Highly recommended!"',
        { multiline: true, maxLength: 360 },
      ),
      field("bookingQuoteAuthor", "Enquiry testimonial author", "— Arun KC., Event Organizer", { maxLength: 100 }),
      field("formNameLabel", "Name label", "Full Name *", { maxLength: 50 }),
      field("formNamePlaceholder", "Name placeholder", "Your name", { maxLength: 80 }),
      field("formPhoneLabel", "Phone label", "Phone *", { maxLength: 50 }),
      field("formPhonePlaceholder", "Phone placeholder", "+977 XXXXX XXXXX", { maxLength: 80 }),
      field("formEmailLabel", "Email label", "Email *", { maxLength: 50 }),
      field("formEmailPlaceholder", "Email placeholder", "you@email.com", { maxLength: 100 }),
      field("formMessageLabel", "Message label", "Message *", { maxLength: 50 }),
      field("formMessagePlaceholder", "Message placeholder", "How can we help you?", { maxLength: 120 }),
      field("formSubmitLabel", "Submit button", "⚡ Send Message", { maxLength: 50 }),
      field("formNote", "Form response note", "We aim to respond within 24 hours.", { maxLength: 160 }),
      field("formSuccessMessage", "Form-success message", "Message sent! We'll get back to you shortly.", { maxLength: 200 }),
    ]),
  },
  {
    id: "box-social-app",
    title: "Testimonials, FAQ and app",
    fields: Object.freeze([
      field("testimonialsEyebrow", "Testimonials eyebrow", "// PLAYER REVIEWS", { maxLength: 60 }),
      field("testimonialsTitle", "Testimonials title", "LOVED BY PLAYERS.", { maxLength: 80 }),
      field("faqEyebrow", "FAQ eyebrow", "// FAQ", { maxLength: 40 }),
      field("faqTitle", "FAQ title", "GOT QUESTIONS?", { maxLength: 80 }),
      field("appEyebrow", "App eyebrow", "// GET THE APP", { maxLength: 60 }),
      field("appTitleLineOne", "App title line 1", "BOOK ANYWHERE.", { maxLength: 70 }),
      field("appTitleLineTwo", "App title line 2", "PLAY EVERYWHERE.", { maxLength: 70 }),
      field(
        "appDescription",
        "App description",
        "Download the ONGROUND app to discover and book BoxArena slots instantly — plus hundreds of sports venues across Nepal.",
        { multiline: true, maxLength: 360 },
      ),
      field("googlePlaySmallLabel", "Google Play small label", "GET IT ON", { maxLength: 30 }),
      field("googlePlayMainLabel", "Google Play main label", "Google Play", { maxLength: 40 }),
      field("appStoreSmallLabel", "App Store small label", "DOWNLOAD ON THE", { maxLength: 40 }),
      field("appStoreMainLabel", "App Store main label", "App Store", { maxLength: 40 }),
      field("appImageAlt", "App image alternative text", "BoxArena turf", { maxLength: 80 }),
      field("appBadgeValue", "App image badge value", "500+", { maxLength: 30 }),
      field("appBadgeLabel", "App image badge label", "Active players monthly", { maxLength: 80 }),
    ]),
  },
  {
    id: "box-contact-footer",
    title: "Contact and footer",
    fields: Object.freeze([
      field("contactEyebrow", "Contact eyebrow", "// GET IN TOUCH", { maxLength: 60 }),
      field("contactTitle", "Contact title", "FIND US.", { maxLength: 80 }),
      field("addressLabel", "Address label", "Address", { maxLength: 40 }),
      field("phoneLabel", "Phone label", "Phone / WhatsApp", { maxLength: 60 }),
      field("instagramLabel", "Instagram label", "Instagram", { maxLength: 50 }),
      field("hoursLabel", "Hours label", "Hours", { maxLength: 40 }),
      field("hoursValue", "Opening-hours fallback value", "6:00 AM – 10:00 PM", { maxLength: 80 }),
      field("hoursPrefix", "Hours prefix", "Every day ·", { maxLength: 60 }),
      field("mapTitle", "Map title", "BoxArena Location", { maxLength: 80 }),
      field("footerTagline", "Footer tagline", "Play. Eat. Repeat.", { maxLength: 80 }),
      field("footerNavigationTitle", "Footer navigation title", "Navigation", { maxLength: 60 }),
      field("footerContactTitle", "Footer contact title", "Contact", { maxLength: 60 }),
      field("footerSocialTitle", "Footer social title", "Socials", { maxLength: 60 }),
      field("footerSocialEmptyText", "Missing-social-links text", "Social links will appear here once added.", { maxLength: 160 }),
      field("facebookLabel", "Facebook label", "Facebook", { maxLength: 40 }),
      field("youtubeLabel", "YouTube label", "YouTube", { maxLength: 40 }),
      field("tiktokLabel", "TikTok label", "TikTok", { maxLength: 40 }),
      field("whatsappLabel", "WhatsApp label", "WhatsApp", { maxLength: 40 }),
      field("footerOpenPrefix", "Footer opening prefix", "Open", { maxLength: 40 }),
      field("footerCopyrightSuffix", "Footer copyright suffix", ". All rights reserved.", { maxLength: 120 }),
      field("footerPoweredBy", "Legacy footer powered-by text", "Powered by ONGROUND", { maxLength: 100 }),
      field("footerPoweredByLabel", "Footer powered-by label", "Powered by", { maxLength: 50 }),
      field("footerPoweredLogoAlt", "Onground-logo alternative text", "ONGROUND", { maxLength: 80 }),
      field("footerPoweredByAriaLabel", "Onground-link accessible label", "Visit Onground website", { maxLength: 100 }),
      field("cityFallback", "City fallback", "Kathmandu", { maxLength: 80 }),
      field("countryFallback", "Country fallback", "Nepal", { maxLength: 80 }),
      field("venueNameFallback", "Venue-name fallback", "BoxArena", { maxLength: 80 }),
      field("addressFallback", "Address fallback", "", { maxLength: 180 }),
      field("phoneFallback", "Phone fallback", "+977-9700426645", { maxLength: 50 }),
      field("openingTimeFallback", "Opening-time fallback", "6:00 AM", { maxLength: 40 }),
      field("closingTimeFallback", "Closing-time fallback", "11:00 PM", { maxLength: 40 }),
    ]),
  },
]);

export const BOX_ARENA_MEDIA_FIELDS = Object.freeze([
  field("logoUrl", "Logo image URL", "", { type: "url", maxLength: 1000 }),
  field("heroImageUrl", "Hero image URL", "", { type: "url", maxLength: 1000 }),
  field("groundImageOneUrl", "Ground fallback image 1 URL", "", { type: "url", maxLength: 1000 }),
  field("groundImageTwoUrl", "Ground fallback image 2 URL", "", { type: "url", maxLength: 1000 }),
  field("groundImageThreeUrl", "Ground fallback image 3 URL", "", { type: "url", maxLength: 1000 }),
  field("appImageUrl", "App section image URL", "", { type: "url", maxLength: 1000 }),
  field("poweredLogoUrl", "Onground footer logo URL", "", { type: "url", maxLength: 1000 }),
]);

export const BOX_ARENA_SOCIAL_FIELDS = Object.freeze([
  field("instagramHandle", "Instagram handle", "", { maxLength: 80 }),
  field("instagramUrl", "Instagram URL", "", { type: "url", maxLength: 1000 }),
  field("facebookUrl", "Facebook URL", "", { type: "url", maxLength: 1000 }),
  field("youtubeUrl", "YouTube URL", "", { type: "url", maxLength: 1000 }),
  field("tiktokUrl", "TikTok URL", "", { type: "url", maxLength: 1000 }),
  field("whatsappUrl", "WhatsApp URL", "", { type: "url", maxLength: 1000 }),
  field("googlePlayUrl", "Google Play URL", "", { type: "url", maxLength: 1000 }),
  field("appStoreUrl", "App Store URL", "", { type: "url", maxLength: 1000 }),
  field("ongroundUrl", "Onground website URL", "https://getonground.com", { type: "url", maxLength: 1000 }),
]);

export const BOX_ARENA_VISIBILITY_FIELDS = Object.freeze([
  { key: "hero", label: "Show hero", fallback: true },
  { key: "grounds", label: "Show grounds", fallback: true },
  { key: "stats", label: "Show statistics", fallback: true },
  { key: "about", label: "Show about", fallback: true },
  { key: "booking", label: "Show enquiry form", fallback: true },
  { key: "testimonials", label: "Show testimonials", fallback: true },
  { key: "faqs", label: "Show FAQs", fallback: true },
  { key: "app", label: "Show app promotion", fallback: true },
  { key: "contact", label: "Show contact", fallback: true },
  { key: "footer", label: "Show footer", fallback: true },
]);

export const BOX_ARENA_ARRAY_DEFINITIONS = Object.freeze({
  sports: Object.freeze({
    title: "Ground presentation overrides",
    addLabel: "Add ground override",
    emptyItem: Object.freeze({ name: "", emoji: "", tagline: "", imageUrl: "" }),
    fields: Object.freeze([
      field("name", "Display name", "", { maxLength: 80 }),
      field("emoji", "Emoji", "", { maxLength: 16 }),
      field("tagline", "Tagline", "", { multiline: true, maxLength: 260 }),
      field("imageUrl", "Image URL", "", { type: "url", maxLength: 1000 }),
    ]),
  }),
  stats: Object.freeze({
    title: "Statistics",
    addLabel: "Add statistic",
    emptyItem: Object.freeze({ value: "", label: "" }),
    fields: Object.freeze([
      field("value", "Value", "", { maxLength: 40 }),
      field("label", "Label", "", { maxLength: 100 }),
    ]),
  }),
  testimonials: Object.freeze({
    title: "Testimonials",
    addLabel: "Add testimonial",
    emptyItem: Object.freeze({ name: "", sport: "", rating: 5, text: "" }),
    fields: Object.freeze([
      field("name", "Name", "", { maxLength: 100 }),
      field("sport", "Sport or role", "", { maxLength: 100 }),
      field("rating", "Rating", "5", { type: "number", maxLength: 1 }),
      field("text", "Testimonial", "", { multiline: true, maxLength: 500 }),
    ]),
  }),
  faqs: Object.freeze({
    title: "FAQs",
    addLabel: "Add FAQ",
    emptyItem: Object.freeze({ question: "", answer: "" }),
    fields: Object.freeze([
      field("question", "Question", "", { maxLength: 220 }),
      field("answer", "Answer", "", { multiline: true, maxLength: 700 }),
    ]),
  }),
  aboutFeatures: Object.freeze({
    title: "About feature badges",
    addLabel: "Add feature badge",
    emptyItem: Object.freeze({ text: "" }),
    fields: Object.freeze([
      field("text", "Feature text", "", { maxLength: 100 }),
    ]),
  }),
  bookingFeatures: Object.freeze({
    title: "Enquiry feature list",
    addLabel: "Add enquiry feature",
    emptyItem: Object.freeze({ text: "" }),
    fields: Object.freeze([
      field("text", "Feature text", "", { maxLength: 120 }),
    ]),
  }),
});

export const PICKLE_POINT_COPY_GROUPS = Object.freeze([
  {
    id: "pickle-navigation-brand",
    title: "Navigation and brand",
    fields: Object.freeze([
      field("logoAriaLabel", "Logo accessibility label", "Pickle Point Nepal", { maxLength: 100 }),
      field("logoPrimaryText", "Logo primary text", "Pickle", { maxLength: 30 }),
      field("logoHighlightText", "Logo highlighted text", "Point", { maxLength: 30 }),
      field("logoSubText", "Logo subtitle", "Nepal · Pulchowk", { maxLength: 80 }),
      field("navCourts", "Courts navigation", "Courts", { maxLength: 24 }),
      field("navAbout", "About navigation", "About", { maxLength: 24 }),
      field("navFaq", "FAQ navigation", "FAQ", { maxLength: 24 }),
      field("navContact", "Contact navigation", "Contact", { maxLength: 24 }),
      field("navCtaLabel", "Navigation CTA", "Book Now", { maxLength: 32 }),
    ]),
  },
  {
    id: "pickle-hero",
    title: "Hero",
    fields: Object.freeze([
      field("heroTag", "Hero location and hours line", "Pulchowk, Lalitpur · Open 6 AM – 10 PM", { maxLength: 140 }),
      field("heroTitleLineOne", "Hero title line 1", "Nepal's", { maxLength: 50 }),
      field("heroTitleHighlight", "Hero highlighted line", "Premier", { maxLength: 50 }),
      field("heroTitleLineThree", "Hero title line 3", "Pickleball", { maxLength: 50 }),
      field(
        "heroDescription",
        "Hero description",
        "Play at Lalitpur's go-to pickleball destination. Fast, fun, and social — perfect for beginners and seasoned players alike. Two premium covered courts await you.",
        { multiline: true, maxLength: 420 },
      ),
      field("heroViewCourtsLabel", "View-courts button", "View Courts", { maxLength: 40 }),
      field("heroBookSlotLabel", "Book-slot button", "Book a Slot", { maxLength: 40 }),
      field("heroCourtCountValue", "Court-count fallback value", "2", { maxLength: 20 }),
      field("heroPriceValue", "Price fallback value", "NPR 1K", { maxLength: 50 }),
      field("heroHoursValue", "Hours fallback value", "16 hrs", { maxLength: 40 }),
      field("heroCourtsLabel", "Court-count label", "Covered Courts", { maxLength: 50 }),
      field("heroPriceLabel", "Price label", "Per Hour", { maxLength: 40 }),
      field("heroHoursLabel", "Opening-hours label", "Open Daily", { maxLength: 40 }),
      field("rentalIcon", "Rental card icon", "🏓", { maxLength: 16 }),
      field("rentalLabel", "Rental card title", "Paddle Rental", { maxLength: 50 }),
      field("rentalPriceText", "Rental card description", "NPR 100 · Balls NPR 150", { maxLength: 100 }),
    ]),
  },
  {
    id: "pickle-about-courts",
    title: "About, amenities and courts",
    fields: Object.freeze([
      field("aboutCourtCountValue", "About badge count fallback", "2", { maxLength: 20 }),
      field("aboutBadgeText", "About image badge", "Premium Courts", { maxLength: 60 }),
      field("aboutEyebrow", "About eyebrow", "About The Venue", { maxLength: 50 }),
      field("aboutTitle", "About title", "Pickleball. Community. Passion.", { maxLength: 100 }),
      field(
        "aboutParagraphOne",
        "About paragraph 1",
        "Pickle Point Nepal is Lalitpur's dedicated pickleball destination at Pulchowk. We built this venue for one purpose — giving Nepal's fastest-growing sports community a premium home to play, train, and connect.",
        { multiline: true, maxLength: 700 },
      ),
      field(
        "aboutParagraphTwo",
        "About paragraph 2",
        "Whether you're picking up a paddle for the first time or chasing competitive glory, our two covered courts and welcoming community are ready for you.",
        { multiline: true, maxLength: 600 },
      ),
      field("amenitiesTitle", "Amenities heading", "Venue Amenities", { maxLength: 70 }),
      field("courtsEyebrow", "Courts eyebrow", "Available Courts", { maxLength: 50 }),
      field("courtsTitle", "Courts title", "Pick Your Court", { maxLength: 80 }),
      field("courtsScrollHint", "Courts scroll hint", "Scroll to explore", { maxLength: 50 }),
      field("courtBookLabel", "Court action button", "View & Book", { maxLength: 40 }),
      field("courtBadge1", "API court 1 badge fallback", "Court 1", { maxLength: 40 }),
      field("courtBadge2", "API court 2 badge fallback", "Court 2", { maxLength: 40 }),
      field("courtBadge3", "API court 3 badge fallback", "Group", { maxLength: 40 }),
    ]),
  },
  {
    id: "pickle-reviews-faq",
    title: "Testimonials and FAQ",
    fields: Object.freeze([
      field("testimonialsEyebrow", "Testimonials eyebrow", "Player Reviews", { maxLength: 50 }),
      field("testimonialsTitle", "Testimonials title", "What Players Say", { maxLength: 90 }),
      field("faqEyebrow", "FAQ eyebrow", "FAQ", { maxLength: 30 }),
      field("faqTitle", "FAQ title", "Got Questions?", { maxLength: 80 }),
      field(
        "faqDescription",
        "FAQ description",
        "Everything you need to know about playing at Pickle Point Nepal. Can't find your answer? Just call us — we're always happy to help.",
        { multiline: true, maxLength: 420 },
      ),
      field("faqContactLabel", "FAQ contact button", "Contact Us", { maxLength: 40 }),
    ]),
  },
  {
    id: "pickle-contact-form",
    title: "Contact and enquiry form",
    fields: Object.freeze([
      field("contactEyebrow", "Contact eyebrow", "Get In Touch", { maxLength: 50 }),
      field("contactTitle", "Contact title", "Book Your Court", { maxLength: 90 }),
      field(
        "contactDescription",
        "Contact description",
        "Ready to play? Send us a message or call directly and we'll get you on court as soon as possible.",
        { multiline: true, maxLength: 360 },
      ),
      field("contactPanelTitleLineOne", "Contact panel title line 1", "Let's", { maxLength: 40 }),
      field("contactPanelTitleLineTwo", "Contact panel title line 2", "Play.", { maxLength: 40 }),
      field(
        "contactPanelDescription",
        "Contact panel description",
        "Two premium covered courts in the heart of Pulchowk. Walk in or book ahead — we're here for you.",
        { multiline: true, maxLength: 360 },
      ),
      field("locationLabel", "Location label", "Location", { maxLength: 40 }),
      field("locationValue", "Location fallback value", "Pulchowk, Lalitpur, Nepal", { maxLength: 180 }),
      field("footerLocationValue", "Footer location fallback", "Pulchowk, Lalitpur", { maxLength: 160 }),
      field("phoneLabel", "Phone label", "Phone", { maxLength: 40 }),
      field("phoneValue", "Phone fallback value", "+977 9700039241", { maxLength: 60 }),
      field("hoursLabel", "Hours label", "Hours", { maxLength: 40 }),
      field("dailyLabel", "Daily suffix", "Daily", { maxLength: 30 }),
      field("formTitle", "Form title", "Send a Message", { maxLength: 70 }),
      field("formNameLabel", "Name label", "Your Name", { maxLength: 50 }),
      field("formNamePlaceholder", "Name placeholder", "Aarav Shrestha", { maxLength: 100 }),
      field("formPhoneLabel", "Form phone label", "Phone", { maxLength: 50 }),
      field("formPhonePlaceholder", "Phone placeholder", "+977 98XX XXXXXX", { maxLength: 100 }),
      field("formEmailLabel", "Email label", "Email Address", { maxLength: 60 }),
      field("formEmailPlaceholder", "Email placeholder", "you@example.com", { maxLength: 120 }),
      field("formMessageLabel", "Message label", "Message", { maxLength: 50 }),
      field(
        "formMessagePlaceholder",
        "Message placeholder",
        "Tell us when you'd like to book, how many players, or any questions...",
        { multiline: true, maxLength: 260 },
      ),
      field("formSubmitLabel", "Submit button", "Send Message", { maxLength: 50 }),
      field(
        "formSuccessMessage",
        "Form-success alert",
        "Message sent! We will get back to you shortly.",
        { maxLength: 220 },
      ),
      field("mapLabel", "Map heading", "📍 Find Us On The Map", { maxLength: 70 }),
      field("mapTitle", "Map accessibility title", "Pickle Point Nepal Location", { maxLength: 100 }),
    ]),
  },
  {
    id: "pickle-app-footer",
    title: "App, social links and footer",
    fields: Object.freeze([
      field("appEyebrow", "App eyebrow", "ONGROUND App", { maxLength: 50 }),
      field("appTitleLineOne", "App title line 1", "Book Courts", { maxLength: 60 }),
      field("appTitleLineTwo", "App title line 2", "On the Go.", { maxLength: 60 }),
      field(
        "appDescription",
        "App description",
        "Download the ONGROUND app to book Pickle Point courts in seconds, manage your schedule, and connect with the pickleball community in Nepal.",
        { multiline: true, maxLength: 420 },
      ),
      field("appStoreIcon", "App Store icon", "🍎", { maxLength: 16 }),
      field("appStoreSmallLabel", "App Store small label", "Download on the", { maxLength: 40 }),
      field("appStoreMainLabel", "App Store main label", "App Store", { maxLength: 40 }),
      field("googlePlayIcon", "Google Play icon", "▶️", { maxLength: 16 }),
      field("googlePlaySmallLabel", "Google Play small label", "Get it on", { maxLength: 40 }),
      field("googlePlayMainLabel", "Google Play main label", "Google Play", { maxLength: 40 }),
      field("appImageAlt", "App image alternative text", "ONGROUND App", { maxLength: 100 }),
      field(
        "footerTagline",
        "Footer tagline",
        "Lalitpur's premier pickleball destination. Two covered courts, one great community.",
        { multiline: true, maxLength: 260 },
      ),
      field("footerQuickLinksTitle", "Footer links title", "Quick Links", { maxLength: 50 }),
      field("footerContactTitle", "Footer contact title", "Contact Info", { maxLength: 50 }),
      field("footerSocialTitle", "Footer social title", "Socials", { maxLength: 50 }),
      field("instagramLabel", "Instagram label", "Instagram", { maxLength: 40 }),
      field("facebookLabel", "Facebook label", "Facebook", { maxLength: 40 }),
      field("tiktokLabel", "TikTok label", "TikTok", { maxLength: 40 }),
      field("whatsappLabel", "WhatsApp label", "WhatsApp", { maxLength: 40 }),
      field("footerCopyrightSuffix", "Copyright suffix", ". All rights reserved.", { maxLength: 120 }),
      field("footerPoweredByLabel", "Powered-by label", "Powered by", { maxLength: 50 }),
      field("poweredLogoAlt", "Powered-by logo alternative text", "ONGROUND", { maxLength: 100 }),
    ]),
  },
]);

export const PICKLE_POINT_MEDIA_FIELDS = Object.freeze([
  field("heroImageUrl", "Hero image URL", "", { type: "url", maxLength: 1000 }),
  field("aboutImageUrl", "About image URL", "", { type: "url", maxLength: 1000 }),
  field("appImageUrl", "App mock-up image URL", "", { type: "url", maxLength: 1000 }),
  field("poweredLogoUrl", "Powered-by logo URL", "", { type: "url", maxLength: 1000 }),
]);

export const PICKLE_POINT_SOCIAL_FIELDS = Object.freeze([
  field("appStoreUrl", "App Store URL", "", { type: "url", maxLength: 1000 }),
  field("googlePlayUrl", "Google Play URL", "", { type: "url", maxLength: 1000 }),
  field("instagramUrl", "Instagram URL", "", { type: "url", maxLength: 1000 }),
  field("facebookUrl", "Facebook URL", "", { type: "url", maxLength: 1000 }),
  field("tiktokUrl", "TikTok URL", "", { type: "url", maxLength: 1000 }),
  field("whatsappUrl", "WhatsApp URL", "", { type: "url", maxLength: 1000 }),
  field("mapEmbedUrl", "Google Maps embed URL", "", { type: "url", maxLength: 1000 }),
]);

export const PICKLE_POINT_ARRAY_DEFINITIONS = Object.freeze({
  courts: Object.freeze({
    title: "Court presentation overrides",
    addLabel: "Add court override",
    emptyItem: Object.freeze({
      name: "",
      sport: "",
      price: "",
      badge: "",
      desc: "",
      imageUrl: "",
    }),
    fields: Object.freeze([
      field("name", "Court name", "", { maxLength: 100 }),
      field("sport", "Sport label", "", { maxLength: 80 }),
      field("price", "Displayed price", "", { maxLength: 80 }),
      field("badge", "Image badge", "", { maxLength: 50 }),
      field("desc", "Description", "", { multiline: true, maxLength: 360 }),
      field("imageUrl", "Image URL", "", { type: "url", maxLength: 1000 }),
    ]),
  }),
  stats: Object.freeze({
    title: "Statistics",
    addLabel: "Add statistic",
    emptyItem: Object.freeze({ n: "", suffix: "", label: "" }),
    fields: Object.freeze([
      field("n", "Number", "", { type: "number", maxLength: 12 }),
      field("suffix", "Suffix", "", { maxLength: 30 }),
      field("label", "Label", "", { maxLength: 100 }),
    ]),
  }),
  amenities: Object.freeze({
    title: "Amenities",
    addLabel: "Add amenity",
    emptyItem: Object.freeze({ icon: "", label: "" }),
    fields: Object.freeze([
      field("icon", "Icon or emoji", "", { maxLength: 16 }),
      field("label", "Label", "", { maxLength: 100 }),
    ]),
  }),
  testimonials: Object.freeze({
    title: "Testimonials",
    addLabel: "Add testimonial",
    emptyItem: Object.freeze({ text: "", name: "", sport: "", rating: 5 }),
    fields: Object.freeze([
      field("text", "Testimonial", "", { multiline: true, maxLength: 600 }),
      field("name", "Name", "", { maxLength: 100 }),
      field("sport", "Sport or role", "", { maxLength: 100 }),
      field("rating", "Rating", "5", { type: "number", maxLength: 1 }),
    ]),
  }),
  faqs: Object.freeze({
    title: "FAQs",
    addLabel: "Add FAQ",
    emptyItem: Object.freeze({ q: "", a: "" }),
    fields: Object.freeze([
      field("q", "Question", "", { maxLength: 220 }),
      field("a", "Answer", "", { multiline: true, maxLength: 700 }),
    ]),
  }),
  marqueeWords: Object.freeze({
    title: "Marquee words",
    addLabel: "Add marquee word",
    emptyItem: Object.freeze({ word: "" }),
    fields: Object.freeze([
      field("word", "Word", "", { maxLength: 30 }),
    ]),
  }),
});

export const MERLION_COPY_GROUPS = Object.freeze([
  {
    id: "merlion-navigation-hero",
    title: "Navigation and hero",
    fields: Object.freeze([
      field("navCourts", "Courts navigation", "Courts", { maxLength: 24 }),
      field("navAbout", "About navigation", "About", { maxLength: 24 }),
      field("navFaq", "FAQ navigation", "FAQ", { maxLength: 24 }),
      field("navContact", "Contact navigation", "Contact", { maxLength: 24 }),
      field("navBookLabel", "Navigation booking button", "Book Now", { maxLength: 40 }),
      field(
        "heroBadge",
        "Hero badge",
        "Indoor Multi-Sports Complex · Lazimpat",
        { maxLength: 120 },
      ),
      field("heroTitleLineOne", "Hero title line 1", "WHERE EVERY", { maxLength: 40 }),
      field("heroTitleLineTwo", "Hero title line 2", "GAME", { maxLength: 40 }),
      field("heroTitleHighlight", "Hero highlighted line", "BEGINS.", { maxLength: 40 }),
      field(
        "heroDescription",
        "Hero description",
        "Kathmandu's premier indoor multi-sport complex. Premium maple hardwood courts for basketball, volleyball, and badminton — all under one roof in Lazimpat.",
        { multiline: true, maxLength: 420 },
      ),
      field("heroViewCourtsLabel", "View-courts button", "View All Courts", { maxLength: 50 }),
      field("heroBookLabel", "Hero booking button", "Book a Session", { maxLength: 50 }),
      field("heroCourtsStatLabel", "Hero courts label", "Courts", { maxLength: 30 }),
      field("heroSportsStatLabel", "Hero sports label", "Sports", { maxLength: 30 }),
      field("heroDailyStatLabel", "Hero daily label", "Daily", { maxLength: 30 }),
    ]),
  },
  {
    id: "merlion-courts-about",
    title: "Courts, statistics and about",
    fields: Object.freeze([
      field("courtsEyebrow", "Courts eyebrow", "AVAILABLE COURTS", { maxLength: 50 }),
      field("courtsCountLabel", "Courts-count suffix", "COURTS.", { maxLength: 30 }),
      field("sportsCountLabel", "Sports-count suffix", "SPORTS.", { maxLength: 30 }),
      field("courtsTitleSuffix", "Courts title ending", "ONE VENUE.", { maxLength: 50 }),
      field(
        "courtsDescription",
        "Courts description",
        "Explore all five of our premium maple hardwood courts across three sports.",
        { multiline: true, maxLength: 320 },
      ),
      field("courtBookLabel", "Court booking button", "Book Now", { maxLength: 40 }),
      field("statsCourtsLabel", "Courts statistic label", "Indoor Courts", { maxLength: 60 }),
      field("statsHoursLabel", "Hours statistic label", "Open Daily", { maxLength: 60 }),
      field("statsSportsLabel", "Sports statistic label", "Sports Offered", { maxLength: 60 }),
      field("statsStartingLabel", "Starting-price statistic label", "Starting (NPR)", { maxLength: 70 }),
      field("aboutEyebrow", "About eyebrow", "ABOUT THE VENUE", { maxLength: 50 }),
      field("aboutTitle", "About title", "INDOOR SPORTS HALL, REDEFINED.", { maxLength: 100 }),
      field(
        "aboutDescription",
        "About description",
        "Merlion Gymnasium is a modern indoor multi-sports complex in Kathmandu offering premium facilities for basketball, badminton, volleyball, and more — ideal for both casual play and competitive training. Our courts feature beautiful maple hardwood flooring with official multi-sport line markings, professional backstops, and stadium-grade LED floodlighting, ensuring an elite sporting experience every visit.",
        { multiline: true, maxLength: 700 },
      ),
      field("aboutBadgeNumber", "About image badge number", "#1", { maxLength: 20 }),
      field("aboutBadgeText", "About image badge text", "In Lazimpat", { maxLength: 60 }),
    ]),
  },
  {
    id: "merlion-reviews-faq",
    title: "Testimonials and FAQs",
    fields: Object.freeze([
      field("testimonialsEyebrow", "Testimonials eyebrow", "PLAYER REVIEWS", { maxLength: 50 }),
      field("testimonialsTitle", "Testimonials title", "WHAT OUR COMMUNITY SAYS.", { maxLength: 100 }),
      field(
        "testimonialsDescription",
        "Testimonials description",
        "Trusted by players, coaches, and sports teams across Kathmandu.",
        { multiline: true, maxLength: 280 },
      ),
      field("faqEyebrow", "FAQ eyebrow", "FAQ", { maxLength: 30 }),
      field("faqTitle", "FAQ title", "QUESTIONS ANSWERED.", { maxLength: 80 }),
      field(
        "faqDescription",
        "FAQ description",
        "Everything you need to know before booking your first session at Merlion Gymnasium.",
        { multiline: true, maxLength: 320 },
      ),
    ]),
  },
  {
    id: "merlion-contact-form",
    title: "Contact and enquiry form",
    fields: Object.freeze([
      field("contactEyebrow", "Contact eyebrow", "GET IN TOUCH", { maxLength: 50 }),
      field("contactTitle", "Contact title", "READY TO PLAY?", { maxLength: 80 }),
      field(
        "contactDescription",
        "Contact description",
        "Reach out to book a court, schedule coaching, or inquire about event hire.",
        { multiline: true, maxLength: 320 },
      ),
      field("contactPanelTitle", "Contact panel title", "LET'S GET ON THE COURT.", { maxLength: 90 }),
      field(
        "contactPanelDescription",
        "Contact panel description",
        "Drop us a message or reach out directly. We respond fast.",
        { multiline: true, maxLength: 280 },
      ),
      field("locationLabel", "Location label", "Location", { maxLength: 40 }),
      field("phoneLabel", "Phone label", "Phone", { maxLength: 40 }),
      field("hoursLabel", "Hours label", "Hours", { maxLength: 40 }),
      field("dailyLabel", "Daily suffix", "Daily", { maxLength: 30 }),
      field("formTitle", "Form title", "Send a Message", { maxLength: 70 }),
      field("formDescription", "Form description", "We'll confirm your booking within the hour.", { maxLength: 180 }),
      field("formNameLabel", "Name label", "Full Name", { maxLength: 50 }),
      field("formNamePlaceholder", "Name placeholder", "Your name", { maxLength: 100 }),
      field("formPhoneLabel", "Form phone label", "Phone", { maxLength: 50 }),
      field("formPhonePlaceholder", "Phone placeholder", "+977", { maxLength: 40 }),
      field("formCourtLabel", "Court-preference label", "Sport / Court Preference", { maxLength: 80 }),
      field("formCourtPlaceholder", "Court-preference placeholder", "Choose a court…", { maxLength: 80 }),
      field("formMessageLabel", "Message label", "Message", { maxLength: 50 }),
      field(
        "formMessagePlaceholder",
        "Message placeholder",
        "Preferred date, time, or any other info...",
        { multiline: true, maxLength: 220 },
      ),
      field("formSubmitLabel", "Submit button", "Send Message", { maxLength: 50 }),
    ]),
  },
  {
    id: "merlion-app-footer",
    title: "App, social links and footer",
    fields: Object.freeze([
      field("appEyebrow", "App eyebrow", "DOWNLOAD THE APP", { maxLength: 50 }),
      field("appTitleLineOne", "App title line 1", "BOOK COURTS", { maxLength: 60 }),
      field("appTitleLineTwo", "App title line 2", "ON YOUR PHONE.", { maxLength: 60 }),
      field(
        "appDescription",
        "App description",
        "Reserve your court at Merlion Gymnasium instantly through the ONGROUND app. Check real-time availability, manage bookings, and connect with other players.",
        { multiline: true, maxLength: 420 },
      ),
      field("appStoreSmallLabel", "App Store small label", "Download on the", { maxLength: 40 }),
      field("appStoreMainLabel", "App Store main label", "App Store", { maxLength: 40 }),
      field("googlePlaySmallLabel", "Google Play small label", "Get it on", { maxLength: 40 }),
      field("googlePlayMainLabel", "Google Play main label", "Google Play", { maxLength: 40 }),
      field(
        "footerTagline",
        "Footer tagline",
        "Kathmandu's premier indoor multi-sport complex. 5 premium maple courts. 3 sports. One venue.",
        { multiline: true, maxLength: 320 },
      ),
      field("footerQuickLinksTitle", "Footer links title", "Quick Links", { maxLength: 50 }),
      field("footerContactTitle", "Footer contact title", "Contact", { maxLength: 50 }),
      field("footerSocialTitle", "Footer social title", "Follow Us", { maxLength: 50 }),
      field("instagramLabel", "Instagram label", "Instagram", { maxLength: 40 }),
      field("facebookLabel", "Facebook label", "Facebook", { maxLength: 40 }),
      field("tiktokLabel", "TikTok label", "TikTok", { maxLength: 40 }),
      field("whatsappLabel", "WhatsApp label", "WhatsApp", { maxLength: 40 }),
      field("footerCopyrightSuffix", "Copyright suffix", ". All rights reserved.", { maxLength: 120 }),
      field("footerPoweredByLabel", "Powered-by label", "Powered by", { maxLength: 50 }),
      field("poweredLogoAlt", "Powered-by logo alternative text", "ONGROUND", { maxLength: 100 }),
    ]),
  },
]);

export const MERLION_MEDIA_FIELDS = Object.freeze([
  field("heroImageUrl", "Hero image URL", "", { type: "url", maxLength: 1000 }),
  field("aboutImageUrl", "About image URL", "", { type: "url", maxLength: 1000 }),
  field("appImageUrl", "App mock-up image URL", "", { type: "url", maxLength: 1000 }),
  field("poweredLogoUrl", "Powered-by logo URL", "", { type: "url", maxLength: 1000 }),
]);

export const MERLION_SOCIAL_FIELDS = Object.freeze([
  field("appStoreUrl", "App Store URL", "", { type: "url", maxLength: 1000 }),
  field("googlePlayUrl", "Google Play URL", "", { type: "url", maxLength: 1000 }),
  field("instagramUrl", "Instagram URL", "", { type: "url", maxLength: 1000 }),
  field("facebookUrl", "Facebook URL", "", { type: "url", maxLength: 1000 }),
  field("tiktokUrl", "TikTok URL", "", { type: "url", maxLength: 1000 }),
  field("whatsappUrl", "WhatsApp URL", "", { type: "url", maxLength: 1000 }),
  field("mapEmbedUrl", "Google Maps embed URL", "", { type: "url", maxLength: 1000 }),
]);

export const MERLION_ARRAY_DEFINITIONS = Object.freeze({
  testimonials: Object.freeze({
    title: "Testimonials",
    addLabel: "Add testimonial",
    emptyItem: Object.freeze({ text: "", name: "", role: "", rating: 5 }),
    fields: Object.freeze([
      field("text", "Testimonial", "", { multiline: true, maxLength: 600 }),
      field("name", "Name", "", { maxLength: 100 }),
      field("role", "Sport or role", "", { maxLength: 100 }),
      field("rating", "Rating", "5", { type: "number", maxLength: 1 }),
    ]),
  }),
  faqs: Object.freeze({
    title: "FAQs",
    addLabel: "Add FAQ",
    emptyItem: Object.freeze({ q: "", a: "" }),
    fields: Object.freeze([
      field("q", "Question", "", { maxLength: 220 }),
      field("a", "Answer", "", { multiline: true, maxLength: 700 }),
    ]),
  }),
  marqueeWords: Object.freeze({
    title: "Marquee words",
    addLabel: "Add marquee word",
    emptyItem: Object.freeze({ word: "" }),
    fields: Object.freeze([
      field("word", "Word", "", { maxLength: 40 }),
    ]),
  }),
});

export const BSA_COPY_GROUPS = Object.freeze([
  {
    id: "bsa-navigation-hero",
    title: "Navigation and hero",
    fields: Object.freeze([
      field("navCourts", "Courts navigation", "Courts", { maxLength: 24 }),
      field("navAbout", "About navigation", "About", { maxLength: 24 }),
      field("navFaq", "FAQ navigation", "FAQ", { maxLength: 24 }),
      field("navContact", "Contact navigation", "Contact", { maxLength: 24 }),
      field("navBookLabel", "Navigation booking button", "BOOK NOW", { maxLength: 40 }),
      field(
        "heroBadge",
        "Hero badge",
        "Premium Badminton Facility · Karyabinayak",
        { maxLength: 120 },
      ),
      field("heroTitleLineOne", "Hero title line 1", "YOUR COURT", { maxLength: 50 }),
      field("heroTitleHighlight", "Hero highlighted line", "IS READY.", { maxLength: 50 }),
      field(
        "heroDescription",
        "Hero description",
        "Lalitpur's premier racket sports academy with professional Li-Ning courts, world-class lighting, and a passionate sporting community.",
        { multiline: true, maxLength: 420 },
      ),
      field("heroReserveLabel", "Hero booking button", "RESERVE NOW", { maxLength: 50 }),
      field("heroExploreLabel", "Hero explore button", "EXPLORE", { maxLength: 50 }),
      field("heroPillLabel", "Hero image pill title", "LI-NING COURTS", { maxLength: 60 }),
      field("heroPillDescription", "Hero image pill description", "Professional Grade", { maxLength: 80 }),
    ]),
  },
  {
    id: "bsa-courts-stats-about",
    title: "Courts, statistics and about",
    fields: Object.freeze([
      field("courtsEyebrow", "Courts eyebrow", "AVAILABLE COURTS", { maxLength: 50 }),
      field("courtsTitle", "Courts title", "CHOOSE YOUR COURT.", { maxLength: 90 }),
      field(
        "courtsDescription",
        "Courts description",
        "Two premium indoor badminton courts with professional-grade Li-Ning surfaces.",
        { multiline: true, maxLength: 320 },
      ),
      field("courtBadgePrefix", "Court badge prefix", "COURT", { maxLength: 30 }),
      field("courtBookLabel", "Court booking button", "BOOK THIS COURT", { maxLength: 50 }),
      field("statsCourtsLabel", "Courts statistic label", "Premium Courts", { maxLength: 60 }),
      field("statsSecondaryLabel", "Secondary statistic label", "Players / Month", { maxLength: 60 }),
      field("statsHoursLabel", "Hours statistic label", "Open Daily", { maxLength: 60 }),
      field("statsStartingLabel", "Starting-price statistic label", "Starting Price (NPR)", { maxLength: 80 }),
      field("aboutEyebrow", "About eyebrow", "ABOUT THE ACADEMY", { maxLength: 60 }),
      field("aboutTitleLineOne", "About title line 1", "PREMIUM BADMINTON", { maxLength: 80 }),
      field("aboutTitleLineTwo", "About title line 2", "FACILITY", { maxLength: 80 }),
      field(
        "aboutDescription",
        "About description",
        "Bhaisepati Sports Academy is a premier racket sports venue in Lalitpur, offering well-maintained badminton courts for players of all skill levels. Whether you're training, playing competitively, or enjoying a casual match with friends, the academy provides a quality sporting environment in the heart of Bhaisepati.",
        { multiline: true, maxLength: 700 },
      ),
      field("aboutBadgeNumber", "About image badge number", "#1", { maxLength: 20 }),
      field("aboutBadgeText", "About image badge text", "In Bhaisepati", { maxLength: 60 }),
    ]),
  },
  {
    id: "bsa-reviews-faq",
    title: "Testimonials and FAQs",
    fields: Object.freeze([
      field("testimonialsEyebrow", "Testimonials eyebrow", "PLAYER REVIEWS", { maxLength: 50 }),
      field("testimonialsTitle", "Testimonials title", "WHAT PLAYERS SAY.", { maxLength: 100 }),
      field(
        "testimonialsDescription",
        "Testimonials description",
        "Hear from our growing community of badminton enthusiasts.",
        { multiline: true, maxLength: 280 },
      ),
      field("faqEyebrow", "FAQ eyebrow", "FAQ", { maxLength: 30 }),
      field("faqTitle", "FAQ title", "GOT QUESTIONS?", { maxLength: 80 }),
      field(
        "faqDescription",
        "FAQ description",
        "Everything you need to know about playing at Bhaisepati Sports Academy.",
        { multiline: true, maxLength: 320 },
      ),
    ]),
  },
  {
    id: "bsa-contact-form",
    title: "Contact and enquiry form",
    fields: Object.freeze([
      field("contactEyebrow", "Contact eyebrow", "GET IN TOUCH", { maxLength: 50 }),
      field("contactTitle", "Contact title", "BOOK YOUR COURT.", { maxLength: 80 }),
      field(
        "contactDescription",
        "Contact description",
        "Ready to play? Reach out or fill the form below to reserve your slot.",
        { multiline: true, maxLength: 320 },
      ),
      field("contactPanelTitle", "Contact panel title", "LET'S PLAY.", { maxLength: 80 }),
      field(
        "contactPanelDescription",
        "Contact panel description",
        "Reach out for bookings, coaching inquiries, or general information. We're here to help.",
        { multiline: true, maxLength: 280 },
      ),
      field("locationLabel", "Location label", "Location", { maxLength: 40 }),
      field("phoneLabel", "Phone label", "Phone", { maxLength: 40 }),
      field("hoursLabel", "Hours label", "Hours", { maxLength: 40 }),
      field("formTitle", "Form title", "SEND A MESSAGE", { maxLength: 70 }),
      field("formDescription", "Form description", "We'll get back to you within 24 hours.", { maxLength: 180 }),
      field("formNameLabel", "Name label", "Full Name", { maxLength: 50 }),
      field("formNamePlaceholder", "Name placeholder", "Your name", { maxLength: 100 }),
      field("formPhoneLabel", "Form phone label", "Phone", { maxLength: 50 }),
      field("formPhonePlaceholder", "Phone placeholder", "+977", { maxLength: 40 }),
      field("formEmailLabel", "Email label", "Email", { maxLength: 50 }),
      field("formEmailPlaceholder", "Email placeholder", "your@email.com", { maxLength: 120 }),
      field("formMessageLabel", "Message label", "Message", { maxLength: 50 }),
      field(
        "formMessagePlaceholder",
        "Message placeholder",
        "Tell us about your booking needs...",
        { multiline: true, maxLength: 220 },
      ),
      field("formSubmitLabel", "Submit button", "SEND MESSAGE", { maxLength: 50 }),
    ]),
  },
  {
    id: "bsa-app-footer",
    title: "App, social links and footer",
    fields: Object.freeze([
      field("appEyebrow", "App eyebrow", "DOWNLOAD THE APP", { maxLength: 50 }),
      field("appTitleLineOne", "App title line 1", "BOOK COURTS", { maxLength: 60 }),
      field("appTitleLineTwo", "App title line 2", "ON THE GO.", { maxLength: 60 }),
      field(
        "appDescription",
        "App description",
        "Use the ONGROUND app to reserve your court anytime, anywhere. View availability, manage bookings, and join the community.",
        { multiline: true, maxLength: 420 },
      ),
      field("appStoreSmallLabel", "App Store small label", "Download on", { maxLength: 40 }),
      field("appStoreMainLabel", "App Store main label", "APP STORE", { maxLength: 40 }),
      field("googlePlaySmallLabel", "Google Play small label", "Get it on", { maxLength: 40 }),
      field("googlePlayMainLabel", "Google Play main label", "GOOGLE PLAY", { maxLength: 40 }),
      field(
        "footerTagline",
        "Footer tagline",
        "Lalitpur's premier badminton destination. Two professional courts, one great community.",
        { multiline: true, maxLength: 320 },
      ),
      field("footerQuickLinksTitle", "Footer links title", "QUICK LINKS", { maxLength: 50 }),
      field("footerContactTitle", "Footer contact title", "CONTACT INFO", { maxLength: 50 }),
      field("footerSocialTitle", "Footer social title", "SOCIALS", { maxLength: 50 }),
      field("instagramLabel", "Instagram label", "Instagram", { maxLength: 40 }),
      field("facebookLabel", "Facebook label", "Facebook", { maxLength: 40 }),
      field("tiktokLabel", "TikTok label", "TikTok", { maxLength: 40 }),
      field("whatsappLabel", "WhatsApp label", "WhatsApp", { maxLength: 40 }),
      field("footerCopyrightSuffix", "Copyright suffix", ". All rights reserved.", { maxLength: 120 }),
      field("footerPoweredByLabel", "Powered-by label", "Powered by", { maxLength: 50 }),
      field("poweredLogoAlt", "Powered-by logo alternative text", "ONGROUND", { maxLength: 100 }),
    ]),
  },
]);

export const BSA_MEDIA_FIELDS = Object.freeze([
  field("heroImageUrl", "Hero main image URL", "", { type: "url", maxLength: 1000 }),
  field("heroFloatImageUrl", "Hero floating image URL", "", { type: "url", maxLength: 1000 }),
  field("aboutImageUrl", "About main image URL", "", { type: "url", maxLength: 1000 }),
  field("aboutFloatImageUrl", "About floating image URL", "", { type: "url", maxLength: 1000 }),
  field("appImageUrl", "App mock-up image URL", "", { type: "url", maxLength: 1000 }),
  field("poweredLogoUrl", "Powered-by logo URL", "", { type: "url", maxLength: 1000 }),
]);

export const BSA_SOCIAL_FIELDS = Object.freeze([
  field("appStoreUrl", "App Store URL", "", { type: "url", maxLength: 1000 }),
  field("googlePlayUrl", "Google Play URL", "", { type: "url", maxLength: 1000 }),
  field("instagramUrl", "Instagram URL", "", { type: "url", maxLength: 1000 }),
  field("facebookUrl", "Facebook URL", "", { type: "url", maxLength: 1000 }),
  field("tiktokUrl", "TikTok URL", "", { type: "url", maxLength: 1000 }),
  field("whatsappUrl", "WhatsApp URL", "", { type: "url", maxLength: 1000 }),
  field("mapEmbedUrl", "Google Maps embed URL", "", { type: "url", maxLength: 1000 }),
]);

export const BSA_ARRAY_DEFINITIONS = Object.freeze({
  testimonials: Object.freeze({
    title: "Testimonials",
    addLabel: "Add testimonial",
    emptyItem: Object.freeze({ text: "", name: "", role: "", rating: 5 }),
    fields: Object.freeze([
      field("text", "Testimonial", "", { multiline: true, maxLength: 600 }),
      field("name", "Name", "", { maxLength: 100 }),
      field("role", "Sport or role", "", { maxLength: 100 }),
      field("rating", "Rating", "5", { type: "number", maxLength: 1 }),
    ]),
  }),
  faqs: Object.freeze({
    title: "FAQs",
    addLabel: "Add FAQ",
    emptyItem: Object.freeze({ q: "", a: "" }),
    fields: Object.freeze([
      field("q", "Question", "", { maxLength: 220 }),
      field("a", "Answer", "", { multiline: true, maxLength: 700 }),
    ]),
  }),
  marqueeWords: Object.freeze({
    title: "Marquee words",
    addLabel: "Add marquee word",
    emptyItem: Object.freeze({ word: "" }),
    fields: Object.freeze([
      field("word", "Word", "", { maxLength: 40 }),
    ]),
  }),
});

export const TURF_PARK_COPY_GROUPS = Object.freeze([
  {
    id: "turf-park-navigation-hero",
    title: "Navigation and hero",
    fields: Object.freeze([
      field("navGrounds", "Grounds navigation", "Grounds", { maxLength: 24 }),
      field("navAbout", "About navigation", "About", { maxLength: 24 }),
      field("navContact", "Contact navigation", "Contact", { maxLength: 24 }),
      field("navReserveLabel", "Navigation booking button", "Reserve Court", { maxLength: 40 }),
      field(
        "heroBadge",
        "Hero badge",
        "5.0 Rating - 26 Reviews - Dholahiti, Lalitpur",
        { maxLength: 160 },
      ),
      field("heroTitleLineOne", "Hero title line 1", "YOUR COURT", { maxLength: 60 }),
      field("heroTitleHighlight", "Hero highlighted line", "AWAITS.", { maxLength: 60 }),
      field(
        "heroDescription",
        "Hero description",
        "Futsal, Basketball, and Pickleball — all under one roof.\nKathmandu Valley's premier indoor sports arena.",
        { multiline: true, maxLength: 500 },
      ),
      field("heroReserveLabel", "Hero booking button", "Reserve Now", { maxLength: 50 }),
      field("heroViewGroundsLabel", "Hero grounds button", "View Grounds", { maxLength: 50 }),
    ]),
  },
  {
    id: "turf-park-grounds-about-stats",
    title: "Grounds, statistics and about",
    fields: Object.freeze([
      field("groundsEyebrow", "Grounds eyebrow", "AVAILABLE COURTS", { maxLength: 50 }),
      field("groundsTitle", "Grounds title", "CHOOSE YOUR SPORT.", { maxLength: 90 }),
      field(
        "groundsDescription",
        "Grounds description",
        "Three premium indoor courts. One incredible venue.",
        { multiline: true, maxLength: 320 },
      ),
      field("groundBookLabel", "Ground booking button", "Book This Court", { maxLength: 50 }),
      field("statsCourtsLabel", "Courts statistic label", "Sports Courts", { maxLength: 60 }),
      field("statsRatingLabel", "Rating statistic label", "Star Rating", { maxLength: 60 }),
      field("statsStartingLabel", "Starting-price statistic label", "Starting (NPR)", { maxLength: 80 }),
      field("statsHoursLabel", "Hours statistic label", "Open Per Day", { maxLength: 60 }),
      field("leagueReadyLabel", "Marquee league label", "League Ready", { maxLength: 50 }),
      field("instantBookingLabel", "Marquee booking label", "Instant Booking", { maxLength: 50 }),
      field("openDailyLabel", "Marquee opening label", "Open Daily", { maxLength: 50 }),
      field("ratedLabel", "Marquee rating suffix", "Rated", { maxLength: 30 }),
      field("sportsLabel", "Marquee sports suffix", "Sports", { maxLength: 30 }),
      field("indoorCourtsLabel", "Marquee indoor label", "Indoor Courts", { maxLength: 50 }),
      field("aboutEyebrow", "About eyebrow", "ABOUT TURF PARK", { maxLength: 60 }),
      field("aboutTitleLineOne", "About title line 1", "MORE THAN", { maxLength: 80 }),
      field("aboutTitleLineTwo", "About title line 2", "A COURT.", { maxLength: 80 }),
      field(
        "aboutDescription",
        "About description",
        "A popular multi-sport indoor arena in Dholahiti, Lalitpur. Turf Park offers quality futsal, basketball, and pickleball courts on well-maintained surfaces. It is a vibrant, energetic venue perfect for casual play, league matches, and team practices across the Kathmandu Valley.",
        { multiline: true, maxLength: 700 },
      ),
    ]),
  },
  {
    id: "turf-park-reviews-faq",
    title: "Testimonials and FAQs",
    fields: Object.freeze([
      field("testimonialsEyebrow", "Testimonials eyebrow", "PLAYER REVIEWS", { maxLength: 50 }),
      field("testimonialsTitle", "Testimonials title", "LOVED BY PLAYERS.", { maxLength: 100 }),
      field("faqEyebrow", "FAQ eyebrow", "FAQ", { maxLength: 30 }),
      field("faqTitle", "FAQ title", "GOT QUESTIONS?", { maxLength: 80 }),
    ]),
  },
  {
    id: "turf-park-contact-form",
    title: "Contact and enquiry form",
    fields: Object.freeze([
      field("contactEyebrow", "Contact eyebrow", "// GET IN TOUCH", { maxLength: 60 }),
      field("contactTitleLineOne", "Contact title line 1", "LET'S", { maxLength: 60 }),
      field("contactTitleLineTwo", "Contact title line 2", "TALK.", { maxLength: 60 }),
      field(
        "contactDescription",
        "Contact description",
        "Have a question or want to discuss a corporate event? Send us a message and our team will get back to you shortly.",
        { multiline: true, maxLength: 420 },
      ),
      field("contactBulletOne", "Contact point 1", "Corporate Tournaments", { maxLength: 100 }),
      field("contactBulletTwo", "Contact point 2", "Birthday Parties & Events", { maxLength: 100 }),
      field("contactBulletThree", "Contact point 3", "Long-term Academy Bookings", { maxLength: 100 }),
      field("contactBulletFour", "Contact point 4", "Sponsorship Opportunities", { maxLength: 100 }),
      field(
        "contactQuote",
        "Contact quote",
        "The Turf Park team was incredibly helpful in organizing our annual corporate futsal tournament. Highly recommended!",
        { multiline: true, maxLength: 500 },
      ),
      field("contactQuoteAuthor", "Contact quote author", "— Arun KC., Event Organizer", { maxLength: 120 }),
      field("formNameLabel", "Name label", "FULL NAME *", { maxLength: 50 }),
      field("formNamePlaceholder", "Name placeholder", "Your name", { maxLength: 100 }),
      field("formPhoneLabel", "Phone label", "PHONE *", { maxLength: 50 }),
      field("formPhonePlaceholder", "Phone placeholder", "+977 XXXXX XXXXX", { maxLength: 80 }),
      field("formEmailLabel", "Email label", "EMAIL *", { maxLength: 50 }),
      field("formEmailPlaceholder", "Email placeholder", "you@email.com", { maxLength: 120 }),
      field("formMessageLabel", "Message label", "MESSAGE *", { maxLength: 50 }),
      field("formMessagePlaceholder", "Message placeholder", "How can we help you?", { multiline: true, maxLength: 220 }),
      field("formSubmitLabel", "Submit button", "Send Message", { maxLength: 50 }),
      field("formFooter", "Form response note", "We aim to respond within 24 hours.", { maxLength: 180 }),
    ]),
  },
  {
    id: "turf-park-app-footer",
    title: "App, social links and footer",
    fields: Object.freeze([
      field("appEyebrow", "App eyebrow", "GET THE APP", { maxLength: 50 }),
      field("appTitleLineOne", "App title line 1", "BOOK FROM", { maxLength: 60 }),
      field("appTitleLineTwo", "App title line 2", "ANYWHERE.", { maxLength: 60 }),
      field(
        "appDescription",
        "App description",
        "Download the ONGROUND app and reserve Turf Park courts in seconds. Explore hundreds of sports venues across the Kathmandu Valley right from your pocket.",
        { multiline: true, maxLength: 420 },
      ),
      field("appStoreSmallLabel", "App Store small label", "Download on the", { maxLength: 40 }),
      field("appStoreMainLabel", "App Store main label", "App Store", { maxLength: 40 }),
      field("googlePlaySmallLabel", "Google Play small label", "Get it on", { maxLength: 40 }),
      field("googlePlayMainLabel", "Google Play main label", "Google Play", { maxLength: 40 }),
      field("appImageAlt", "App image alternative text", "Onground App Mockup", { maxLength: 100 }),
      field("footerTagline", "Footer tagline", "Play. Compete. Repeat.", { maxLength: 220 }),
      field("footerNavigationTitle", "Footer navigation title", "Navigation", { maxLength: 50 }),
      field("footerContactTitle", "Footer contact title", "Contact", { maxLength: 50 }),
      field("footerSocialTitle", "Footer social title", "Follow Us", { maxLength: 50 }),
      field("instagramLabel", "Instagram label", "Instagram", { maxLength: 40 }),
      field("facebookLabel", "Facebook label", "Facebook", { maxLength: 40 }),
      field("tiktokLabel", "TikTok label", "TikTok", { maxLength: 40 }),
      field("whatsappLabel", "WhatsApp label", "WhatsApp", { maxLength: 40 }),
      field("footerCopyrightSuffix", "Copyright suffix", ". All rights reserved.", { maxLength: 120 }),
      field("footerPoweredByLabel", "Powered-by label", "Powered by", { maxLength: 50 }),
      field("poweredLogoAlt", "Powered-by logo alternative text", "ONGROUND", { maxLength: 100 }),
    ]),
  },
]);

export const TURF_PARK_MEDIA_FIELDS = Object.freeze([
  field("heroImageUrl", "Hero image URL", "", { type: "url", maxLength: 1000 }),
  field("appImageUrl", "App mock-up image URL", "", { type: "url", maxLength: 1000 }),
  field("poweredLogoUrl", "Powered-by logo URL", "", { type: "url", maxLength: 1000 }),
]);

export const TURF_PARK_SOCIAL_FIELDS = Object.freeze([
  field("appStoreUrl", "App Store URL", "", { type: "url", maxLength: 1000 }),
  field("googlePlayUrl", "Google Play URL", "", { type: "url", maxLength: 1000 }),
  field("instagramUrl", "Instagram URL", "", { type: "url", maxLength: 1000 }),
  field("facebookUrl", "Facebook URL", "", { type: "url", maxLength: 1000 }),
  field("tiktokUrl", "TikTok URL", "", { type: "url", maxLength: 1000 }),
  field("whatsappUrl", "WhatsApp URL", "", { type: "url", maxLength: 1000 }),
  field("mapEmbedUrl", "Google Maps embed URL", "", { type: "url", maxLength: 1000 }),
]);

export const TURF_PARK_ARRAY_DEFINITIONS = Object.freeze({
  testimonials: Object.freeze({
    title: "Testimonials",
    addLabel: "Add testimonial",
    emptyItem: Object.freeze({ text: "", name: "", sport: "" }),
    fields: Object.freeze([
      field("text", "Testimonial", "", { multiline: true, maxLength: 600 }),
      field("name", "Name", "", { maxLength: 100 }),
      field("sport", "Sport or role", "", { maxLength: 100 }),
    ]),
  }),
  faqs: Object.freeze({
    title: "FAQs",
    addLabel: "Add FAQ",
    emptyItem: Object.freeze({ q: "", a: "" }),
    fields: Object.freeze([
      field("q", "Question", "", { maxLength: 220 }),
      field("a", "Answer", "", { multiline: true, maxLength: 700 }),
    ]),
  }),
});

export const BOXARENA_COURTLINE_COPY_GROUPS = Object.freeze([
  {
    id: "boxarena-courtline-navigation-hero",
    title: "Navigation and hero",
    fields: Object.freeze([
      field("navAbout", "About navigation", "About", { maxLength: 24 }),
      field("navFaq", "FAQ navigation", "FAQ", { maxLength: 24 }),
      field("navLocation", "Location navigation", "Location", { maxLength: 24 }),
      field("navBookLabel", "Navigation booking button", "Book a Court", { maxLength: 40 }),
      field("heroEyebrow", "Hero location eyebrow", "Kathmandu, Nepal", { maxLength: 100 }),
      field("heroTitleLineOne", "Hero title line 1", "Book the court.", { maxLength: 70 }),
      field("heroTitleLineTwo", "Hero title line 2", "Own the game.", { maxLength: 70 }),
      field(
        "heroDescription",
        "Hero description",
        "Badminton, futsal and box cricket on regulation-marked courts, open every day from 6am to midnight. Walk in, warm up, play.",
        { multiline: true, maxLength: 500 },
      ),
      field("heroPrimaryLabel", "Hero booking button", "Check availability", { maxLength: 50 }),
      field("heroSecondaryLabel", "Hero venue button", "See the venue", { maxLength: 50 }),
      field("statCourtsLabel", "Courts statistic label", "Courts", { maxLength: 50 }),
      field("statHoursLabel", "Hours statistic label", "Hrs open daily", { maxLength: 50 }),
      field("statSportsLabel", "Sports statistic label", "Sports", { maxLength: 50 }),
      field("statDaysLabel", "Days statistic label", "Days a week", { maxLength: 50 }),
    ]),
  },
  {
    id: "boxarena-courtline-about-gallery",
    title: "About and gallery",
    fields: Object.freeze([
      field("aboutEyebrow", "About eyebrow", "About the venue", { maxLength: 50 }),
      field("aboutTitle", "About title", "Built for players, not spectators", { maxLength: 100 }),
      field(
        "aboutParagraphOne",
        "About paragraph 1",
        "BOXARENA opened in 2019 as a single badminton hall and grew into a four-sport arena by popular demand. There is no membership desk to get past and no minimum booking — just courts, kept in match condition, ready when you are.",
        { multiline: true, maxLength: 700 },
      ),
      field(
        "aboutParagraphTwo",
        "About paragraph 2",
        "Coaching sessions run in the mornings, corporate leagues take over on weeknights, and the courts stay open to walk-ins the rest of the time. Shoes, rackets and bibs are available to rent at the front desk.",
        { multiline: true, maxLength: 700 },
      ),
      field("galleryEyebrow", "Gallery eyebrow", "Take a look around", { maxLength: 60 }),
      field("galleryTitle", "Gallery title", "Our Gallery", { maxLength: 80 }),
    ]),
  },
  {
    id: "boxarena-courtline-faq-location",
    title: "FAQs and location",
    fields: Object.freeze([
      field("faqEyebrow", "FAQ eyebrow", "Before you book", { maxLength: 60 }),
      field("faqTitle", "FAQ title", "Frequently asked", { maxLength: 80 }),
      field("locationEyebrow", "Location eyebrow", "Find us", { maxLength: 50 }),
      field("locationTitle", "Location title", "On the ground", { maxLength: 80 }),
      field("addressLabel", "Address label", "Address", { maxLength: 40 }),
      field("hoursLabel", "Hours label", "Hours", { maxLength: 40 }),
      field("phoneLabel", "Phone label", "Phone", { maxLength: 40 }),
      field("emailLabel", "Email label", "Email", { maxLength: 40 }),
      field(
        "footerVenueLine",
        "Footer venue line fallback",
        "Kathmandu, Nepal — Open 6am to midnight, daily",
        { maxLength: 180 },
      ),
      field("footerCopyrightSuffix", "Copyright suffix", ".", { maxLength: 80 }),
    ]),
  },
]);

export const BOXARENA_COURTLINE_MEDIA_FIELDS = Object.freeze([
  field("logoUrl", "Venue logo URL", "", { type: "url", maxLength: 1000 }),
  field("heroPlayerImageUrl", "Hero player artwork URL", "", { type: "url", maxLength: 1000 }),
  field("aboutPlayerImageUrl", "About player artwork URL", "", { type: "url", maxLength: 1000 }),
]);

export const BOXARENA_COURTLINE_SOCIAL_FIELDS = Object.freeze([
  field("contactEmail", "Contact email", "", { type: "email", maxLength: 160 }),
  field("mapEmbedUrl", "Google Maps embed URL", "", { type: "url", maxLength: 1000 }),
]);

export const BOXARENA_COURTLINE_ARRAY_DEFINITIONS = Object.freeze({
  faqs: Object.freeze({
    title: "FAQs",
    addLabel: "Add FAQ",
    emptyItem: Object.freeze({ q: "", a: "" }),
    fields: Object.freeze([
      field("q", "Question", "", { maxLength: 220 }),
      field("a", "Answer", "", { multiline: true, maxLength: 700 }),
    ]),
  }),
});
